import { memo, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { ScrollableElementProvider } from 'containers/SubSidebar/AIVideoContent/context'
import { FETCH_LIMIT } from 'containers/SubSidebar/AiLibContent/consts'
import { aiImageThunks } from 'store/features/aiImage/thunks'
import { TAppDispatch } from 'store/types'
import { aiLibImageSelectors } from 'store/features/aiLibImage/selectors'
import { aiLibImageThunks } from 'store/features/aiLibImage/thunks'
import * as Styled from './styled'
import { useFetchAiLibHistoryOnScroll } from './hooks/useFetchAiLibHistoryOnScroll'
import { IAIImageProject } from 'store/types/aiImage'
import ImageItem from './ImageItem'
import { usePreviewNavigation } from '../hooks/usePreviewNavigation'
import { NotFound } from './NotFound'

const ImagesTab = () => {
  const [scrollContainer, setScrollContainer] = useState<HTMLDivElement | null>(
    null
  )
  const dispatch = useDispatch<TAppDispatch>()
  const scrollableElementRef = useMemo(
    () => ({ current: scrollContainer }),
    [scrollContainer]
  )
  useFetchAiLibHistoryOnScroll(scrollContainer)

  const isInitialLoading = useSelector(aiLibImageSelectors.isInitialLoading)
  useEffect(() => {
    if (isInitialLoading && scrollContainer) {
      scrollContainer.scrollTop = 0
    }
  }, [isInitialLoading, scrollContainer])

  const isLoaded = useSelector(aiLibImageSelectors.isLoaded)
  const historyList = useSelector(aiLibImageSelectors.historyList)

  const {
    activePreviewIndex,
    handleOpenPreview,
    handleClosePreview,
    handleNextPreview,
    handlePrevPreview
  } = usePreviewNavigation(historyList.length)

  useEffect(() => {
    if (!isLoaded) {
      dispatch(aiLibImageThunks.getHistory({ offset: 0, limit: FETCH_LIMIT }))
    }
  }, [dispatch, isLoaded])

  useEffect(() => {
    dispatch(aiImageThunks.getModels())
    dispatch(aiImageThunks.getStyles())
  }, [dispatch])

  return (
    <ScrollableElementProvider scrollableElementRef={scrollableElementRef}>
      <Styled.Container>
        <Styled.ItemsContainer ref={setScrollContainer}>
          {historyList.length === 0 && isLoaded ? (
            <NotFound />
          ) : (
            <>
              {historyList.map((item: IAIImageProject, index: number) => (
                <ImageItem
                  key={item.id}
                  item={item}
                  index={index}
                  isPreviewOpen={activePreviewIndex === index}
                  onOpenPreview={handleOpenPreview}
                  onClosePreview={handleClosePreview}
                  onNext={handleNextPreview}
                  onPrev={handlePrevPreview}
                  hasNext={index < historyList.length - 1}
                  hasPrev={index > 0}
                />
              ))}
            </>
          )}
        </Styled.ItemsContainer>
      </Styled.Container>
    </ScrollableElementProvider>
  )
}

export default memo(ImagesTab)
